// src/components/ReportModal.jsx
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useReportPostMutation } from '../store/api/postApi';
import { toast } from 'react-toastify';
import styles from './ReportModal.module.css';

const REASONS = [
  { value: 'Spam',             label: '🚫 Spam',                    desc: 'Repetitive, unwanted, or promotional content' },
  { value: 'Hate Speech',      label: '🤬 Hate Speech',             desc: 'Attacks people based on identity' },
  { value: 'Misinformation',   label: '❌ Misinformation',          desc: 'False or misleading information' },
  { value: 'Harassment',       label: '😡 Harassment or Bullying',  desc: 'Targeting or threatening someone' },
  { value: 'Nudity',           label: '🔞 Nudity / Sexual Content', desc: 'Explicit or adult content' },
  { value: 'Violence',         label: '⚠️ Violence',                desc: 'Graphic or dangerous content' },
  { value: 'Other',            label: '📝 Other',                   desc: 'Something else not listed above' },
];

export default function ReportModal({ postId, postType, onClose }) {
  const [selectedReason, setSelectedReason] = useState('');
  const [description, setDescription]       = useState('');
  const [submitted, setSubmitted]           = useState(false);

  const [reportPost, { isLoading }] = useReportPostMutation();

  const handleSubmit = async () => {
    if (!selectedReason) return;

    try {
      const result = await reportPost({
        postId,
        postType: postType || 'Post',   // 'Post' or 'SharedPost'
        reason: selectedReason,
        description: description.trim() || undefined,
      }).unwrap();

      setSubmitted(true);

      // Original post locked (10+ reports)
      if (result?.isLocked) {
        toast.info('This post has been reviewed and hidden from the community.');
      }
      // Shared post removed (10+ reports)
      if (result?.isRemoved) {
        toast.info('This shared post has been removed from the community.');
      }
    } catch (err) {
      toast.error('Failed to submit report. Please try again.');
    }
  };

  return createPortal(
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Report post">

        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>Report Post</h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {submitted ? (
          /* ── Success state ── */
          <div className={styles.success}>
            <div className={styles.successIcon}>✅</div>
            <h3>Thanks for letting us know</h3>
            <p>
              Your report has been submitted. We review all reports carefully
              and take action when our community guidelines are violated.
            </p>
            <button className={styles.doneBtn} onClick={onClose}>Done</button>
          </div>
        ) : (
          /* ── Report form ── */
          <>
            <div className={styles.body}>
              <p className={styles.subtitle}>Why are you reporting this post?</p>

              <div className={styles.reasons}>
                {REASONS.map(r => (
                  <button
                    key={r.value}
                    className={`${styles.reasonBtn} ${selectedReason === r.value ? styles.selected : ''}`}
                    onClick={() => setSelectedReason(r.value)}
                  >
                    <div className={styles.reasonLeft}>
                      <span className={styles.reasonLabel}>{r.label}</span>
                      <span className={styles.reasonDesc}>{r.desc}</span>
                    </div>
                    <div className={styles.reasonRadio}>
                      {selectedReason === r.value && <div className={styles.radioDot} />}
                    </div>
                  </button>
                ))}
              </div>

              {selectedReason && (
                <div className={styles.descWrapper}>
                  <label className={styles.descLabel}>
                    Additional details <span className={styles.optional}>(optional)</span>
                  </label>
                  <textarea
                    className={styles.descInput}
                    placeholder="Tell us more about what's happening..."
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    maxLength={500}
                    rows={3}
                  />
                  <span className={styles.charCount}>{description.length}/500</span>
                </div>
              )}
            </div>

            <div className={styles.footer}>
              <button className={styles.cancelBtn} onClick={onClose}>Cancel</button>
              <button
                className={styles.submitBtn}
                onClick={handleSubmit}
                disabled={!selectedReason || isLoading}
              >
                {isLoading ? 'Submitting…' : 'Submit Report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}